import { FetchArgs } from '@reduxjs/toolkit/query/react'
import {
  CourseThemeDetailed,
  CourseThemeListItemServerResponse,
  CourseThemesQueryParams,
  CreateCourseThemeBody,
  CreateFinalTestCourseChapterThemeBody,
  UpdateFinalTestCourseThemeBody,
  UpdateCourseThemeBody,
} from '@models/courseTheme'
import { ItemsFromResponse } from '@models/common'
import StoreTags from '@enums/StoreTags'
import { HYDRA_MEMBER, HYDRA_TOTAL_ITEMS } from '../../config/consts'
import emptySplitApi from '../api'

const BASE_URL = '/api/course_chapter_themes'

const getIdFromIri = (value: string | number): number =>
  typeof value === 'number' ? value : Number(value.split('/').pop())

type CourseThemeByIdQueryParams =
  | (string | number)
  | {
      id: string | number
      forEmployeeEducation?: string
    }

export const courseThemesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getCourseThemes: builder.query<
      {
        items: CourseThemeListItemServerResponse[]
        length: number
      },
      CourseThemesQueryParams
    >({
      providesTags: [StoreTags.COURSE_THEMES],
      query: (params) => ({
        url: BASE_URL,
        params,
      }),
      transformResponse: (
        response: ItemsFromResponse<CourseThemeListItemServerResponse>
      ) => ({
        items: response[HYDRA_MEMBER],
        length: response[HYDRA_TOTAL_ITEMS],
      }),
    }),
    getCourseThemeById: builder.query<CourseThemeDetailed, CourseThemeByIdQueryParams>({
      providesTags: [StoreTags.COURSE_THEME],
      keepUnusedDataFor: 1,
      query: (args) => {
        if (typeof args === 'string' || typeof args === 'number') {
          return `${BASE_URL}/${args}`
        }

        const params = args.forEmployeeEducation
          ? { forEmployeeEducation: args.forEmployeeEducation }
          : undefined

        return {
          url: `${BASE_URL}/${args.id}`,
          params,
        }
      },
    }),
    postCourseTheme: builder.mutation<
      CourseThemeDetailed,
      CreateCourseThemeBody
    >({
      invalidatesTags: (result) =>
        result
          ? [
              StoreTags.COURSE_THEMES,
              StoreTags.COURSE_CHAPTERS,
              StoreTags.COURSES,
              StoreTags.COURSE,
              StoreTags.MY_COURSE,
            ]
          : [],
      query: (body): FetchArgs => ({
        url: BASE_URL,
        method: 'POST',
        body,
      }),
    }),
    putCourseTheme: builder.mutation<
      CourseThemeDetailed,
      UpdateCourseThemeBody
    >({
      invalidatesTags: (result) =>
        result
          ? [StoreTags.COURSE_THEME]
          : [],
      query: (body): FetchArgs => ({
        url: `${BASE_URL}/${body.id}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        const chapterId = getIdFromIri(body.courseChapter)

        if (!chapterId) return

        try {
          const { data } = await queryFulfilled

          dispatch(
            courseThemesApi.util.updateQueryData(
              'getCourseThemes',
              { pagination: false, courseChapter: chapterId },
              (draft) => {
                const theme = draft.items.find(
                  (item) => Number(item.id) === Number(body.id)
                )

                if (theme) {
                  theme.title = data.title
                  theme.hoursTime = data.hoursTime
                }
              }
            )
          )
        } catch {
          // Invalidations handle error state; keep existing list cache untouched.
        }
      },
    }),
    deleteCourseTheme: builder.mutation<void, number>({
      invalidatesTags: [
        StoreTags.COURSE_THEMES,
        StoreTags.COURSE_CHAPTERS,
        StoreTags.EMPLOYEE_EDUCATIONS,
        StoreTags.COURSES,
        StoreTags.MY_COURSE,
      ],
      query: (id): FetchArgs => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
      }),
    }),
    postFinalTestCourseTheme: builder.mutation<
      CourseThemeDetailed,
      CreateFinalTestCourseChapterThemeBody
    >({
      invalidatesTags: (result) =>
        result ? [StoreTags.COURSE_THEMES, StoreTags.MY_COURSE] : [],
      query: (body): FetchArgs => ({
        url: BASE_URL,
        method: 'POST',
        body,
      }),
    }),
    putFinalTestCourseTheme: builder.mutation<
      CourseThemeDetailed,
      UpdateFinalTestCourseThemeBody
    >({
      invalidatesTags: (result) =>
        result ? [StoreTags.COURSE_THEMES, StoreTags.MY_COURSE] : [],
      query: ({ id, ...body }): FetchArgs => ({
        url: `${BASE_URL}/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    reorderCourseChapterThemes: builder.mutation<
      { success: boolean },
      { chapterId: number; themeIds: number[] }
    >({
      invalidatesTags: (result) =>
        result
          ? [StoreTags.COURSE_THEMES, StoreTags.MY_COURSE]
          : [],
      query: ({ chapterId, themeIds }): FetchArgs => ({
        url: `/api/course_chapters/${chapterId}/themes/reorder`,
        method: 'POST',
        body: { themeIds },
      }),
    }),
  }),
})

export const {
  useGetCourseThemesQuery,
  useDeleteCourseThemeMutation,
  usePostCourseThemeMutation,
  usePutCourseThemeMutation,
  useGetCourseThemeByIdQuery,
  useLazyGetCourseThemeByIdQuery,
  useReorderCourseChapterThemesMutation,
  usePostFinalTestCourseThemeMutation,
  usePutFinalTestCourseThemeMutation,
} = courseThemesApi
